import { useContext, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FREE_GROUP_KEYS } from 'getting-started-shared-tasks/gettingStartedGroupKeys';
import TaskApiContext from '../contexts/TaskApiContext';
import TaskDataContext from '../contexts/TaskDataContext';
import { getTaskCompletion, getTaskGroupCompletion, getTasksRemainingProgress } from '../utils/taskUtils';
import { useQueryParams } from './history';
import { getTaskChildGroupExperimentFilterMap } from '../constants/experimentConstants';
import { jsx as _jsx } from "react/jsx-runtime";
// e.g. The 'Create Your Account' Task
const NUMBER_OF_PRECOMPLETED_BASICS_TASKS = 1;
const CRM_SETUP_ONBOARDING_VIAS = ['post-crm-setup-tour', 'in-app-crm-setup-sidebar', 'set-up-crm',
// ACQ149 Post-Setup Tours
'post-setup-contacts-tour'];
export const useTaskApi = () => {
  return useContext(TaskApiContext);
};
export const useTaskData = () => {
  return useContext(TaskDataContext);
};
export const reduceTaskMap = (tasks, taskMap) => {
  // Takes the taskMap from the taskData api hook and reduces it to
  // contain only the tasks that are in the tasks array
  return tasks.reduce((reducedTaskMap, task) => {
    reducedTaskMap[task.key] = taskMap[task.key];
    return reducedTaskMap;
  }, {});
};
export const useTaskDataByGroupKey = groupKey => {
  const {
    taskGroups = [],
    taskMap
  } = useTaskData();
  const taskData = [...taskGroups, ...taskGroups.flatMap(({
    childGroups
  }) => childGroups)].find(({
    key
  }) => key === groupKey) || {};
  return Object.assign({}, taskData, {
    taskMap: reduceTaskMap(taskData.tasks || [], taskMap)
  });
};
export const useNextTaskGroupData = currentGroupKey => {
  const {
    taskGroups = []
  } = useTaskData();
  const flattenedTaskGroupViews = taskGroups.flatMap(({
    childGroups
  }) => {
    return childGroups.map(({
      key
    }) => key);
  });
  const currentTaskGroupIndex = flattenedTaskGroupViews.findIndex(groupKey => groupKey === currentGroupKey);
  const nextTaskGroupKey = flattenedTaskGroupViews[currentTaskGroupIndex + 1];
  return useTaskDataByGroupKey(nextTaskGroupKey);
};

// This method is intended to enable running "experiment" child groups
// The TaskChildGroupExperimentFilterMap will contain the filtering logic for the associated groups
export const filterTaskDataByGroupKey = (taskData, treatments, logExposure) => {
  const filteredTaskData = Object.assign({}, taskData);
  const TaskChildGroupExperimentFilterMap = getTaskChildGroupExperimentFilterMap();
  // Apply the filtering logic for each experiment
  Object.entries(TaskChildGroupExperimentFilterMap).forEach(([experimentKey, filter]) => {
    var _treatments$experimen;
    const variant = (_treatments$experimen = treatments[experimentKey]) === null || _treatments$experimen === void 0 || (_treatments$experimen = _treatments$experimen.parameters) === null || _treatments$experimen === void 0 ? void 0 : _treatments$experimen['group'];
    filter === null || filter === void 0 || filter(filteredTaskData, variant, logExposure);
  });
  return filteredTaskData;
};
export const useGroupKey = () => {
  const {
    groupKey
  } = useTaskData();
  return groupKey;
};
export const useProgress = () => {
  const {
    isLoading,
    taskProgress
  } = useTaskData();
  return useMemo(() => {
    if (isLoading || !taskProgress) {
      return {
        actionedTasksCount: 0,
        progressRate: 0,
        tasksCount: 0
      };
    }
    const tasksCount = taskProgress.tasksCompleted.length + taskProgress.tasksSkipped.length + taskProgress.tasksUncompleted.length;
    const uncompletedTasksCount = taskProgress.tasksUncompleted.length;
    const actionedTasksCount = tasksCount - uncompletedTasksCount;
    const progressRate = tasksCount ? Math.min(Math.round(actionedTasksCount / tasksCount * 100), 100) : 0;
    return {
      actionedTasksCount,
      progressRate,
      tasksCount,
      uncompletedTasksCount
    };
  }, [isLoading, taskProgress]);
};
export const useTaskGroupProgress = taskGroup => {
  const {
    isLoading,
    taskProgress: allTaskProgress
  } = useTaskData();
  const {
    childGroups,
    tasks
  } = taskGroup;
  if (isLoading || !allTaskProgress) {
    return {
      completedTasks: [],
      skippedTasks: [],
      uncompletedTasks: [],
      completedTaskGroups: [],
      uncompletedTaskGroups: [],
      remainingDuration: 0,
      percentageComplete: 0,
      tasksCompleted: 0,
      totalTasks: 0
    };
  }
  const {
    tasksCompleted,
    tasksSkipped
  } = allTaskProgress;
  const taskProgress = getTaskCompletion(tasks, tasksCompleted, tasksSkipped);
  const tasksRemainingProgress = getTasksRemainingProgress(taskProgress);
  const childGroupProgress = getTaskGroupCompletion(childGroups, tasksCompleted, tasksSkipped);
  return Object.assign({}, taskProgress, tasksRemainingProgress, childGroupProgress);
};
export const useChecklistEstimation = () => {
  const {
    isLoading,
    taskMap = {},
    taskProgress: {
      tasksUncompleted
    } = {}
  } = useTaskData();
  const {
    actionedTasksCount,
    tasksCount,
    progressRate
  } = useProgress();
  return useMemo(() => {
    if (isLoading) {
      return {
        progressRate: 0,
        remainingDuration: 0,
        tasksCompleted: 0,
        totalTasks: 0
      };
    }
    return Object.keys(taskMap).reduce((estimation, taskKey) => {
      if (tasksUncompleted && tasksUncompleted.includes(taskKey)) {
        estimation.remainingDuration += taskMap[taskKey].durationInMinutes || 0;
      }
      return estimation;
    }, {
      progressRate,
      remainingDuration: 0,
      tasksCompleted: actionedTasksCount,
      totalTasks: tasksCount
    });
  }, [isLoading, taskMap, tasksUncompleted, actionedTasksCount, tasksCount, progressRate]);
};

/**
 * Filter out all uncompleted and skipped tasks from task's dependencies config
 * @param {Array | null} dependencyTasks
 * @returns {Array | null} null or non-empty array of all dependency tasks config
 */
export const useUncompletedDependencyTasks = dependencyTasks => {
  const {
    taskProgress
  } = useTaskData();
  return useMemo(() => {
    if (!taskProgress || !Array.isArray(dependencyTasks) || dependencyTasks.length === 0) {
      return null;
    }
    const {
      tasksUncompleted,
      tasksSkipped
    } = taskProgress;
    const uncompletedDependencyTasks = dependencyTasks.filter(({
      key
    }) => tasksUncompleted.includes(key) || tasksSkipped.includes(key));
    if (uncompletedDependencyTasks.length === 0) {
      return null;
    }
    return uncompletedDependencyTasks;
  }, [dependencyTasks, taskProgress]);
};
export const useTaskGroupCta = groupKey => {
  const taskGroupData = useTaskDataByGroupKey(groupKey);
  const {
    completedTasks,
    skippedTasks,
    uncompletedTasks
  } = useTaskGroupProgress(taskGroupData);
  const {
    via = ''
  } = useQueryParams();
  const viaQueryParams = Array.isArray(via) ? via : [via];
  const isBasicsTasks = groupKey === FREE_GROUP_KEYS.setUpTheBasics;
  const isViaPostCrmSetupTour = CRM_SETUP_ONBOARDING_VIAS.some(crmSetupVia => viaQueryParams.includes(crmSetupVia));
  const numberOfDoneTasks = completedTasks.length + skippedTasks.length;
  const hasNoUserCompletedTasks = numberOfDoneTasks === NUMBER_OF_PRECOMPLETED_BASICS_TASKS;
  const hasSomeCompletedTasks = numberOfDoneTasks > NUMBER_OF_PRECOMPLETED_BASICS_TASKS && uncompletedTasks.length > 0;
  if (isBasicsTasks && hasNoUserCompletedTasks) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "setup-guide-components.consolidatedUserGuide.taskViewCard.startTask"
    });
  }

  // These CRM Setup users will have some tasks completed by automation, so we
  // show them how to start their tasks even though some will be 'done' when
  // they open the task side panel
  if (isBasicsTasks && isViaPostCrmSetupTour && hasSomeCompletedTasks) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "setup-guide-components.consolidatedUserGuide.taskViewCard.continueTasks"
    });
  }
  return null;
};